import md5 from 'js-md5';
import { Toast } from 'react-vant';
import config from './config';

/**
 * web 调试模式的请求实现,复刻 h5-proxy(call_http_proxy.js)的等价流程:
 *   1. login(): POST /accountApi/V3/userLoginPwd → token
 *   2. POST /h5/proxy/api/request { token, channel, uuid, ts, sig, data: { requestPath, requestData } }
 *
 * 响应契约与 bridge 模式(clientRequest.js)保持一致:
 *   - 成功:resolve({ error, errorCode, resultBodyObject })
 *   - 业务失败:Toast + reject
 *
 * 说明:错误码映射与 clientRequest.js 相同,这里刻意独立实现以解耦。
 */

const md5Hex = (text) => md5(String(text));

// Mock 开关在 webpack 层（REACT_APP_MOCK=true），此为构建时内联，dev 时走 Node mock 服务
const isMock = process.env.REACT_APP_MOCK === 'true';

// 拼装完整后端地址:mock 时走相对路径经 devServer 代理到 Node 服务，否则走 baseUrl
const apiUrl = (path) => (isMock ? path : `${(config.baseUrl || '').replace(/\/+$/, '')}${path}`);

let cachedToken = null;
let loginPromise = null;

async function login() {
  const password = md5Hex(`${config.passwordPrefix || ''}${config.password}${config.signKey}`);
  const ts = Date.now();
  const cis = `${config.deviceType}${config.email}null${password}`;
  const sig = md5Hex(`${cis}${ts}${config.signKey}`);

  const resp = await fetch(apiUrl('/accountApi/V3/userLoginPwd'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deviceType: config.deviceType,
      channel: config.channel,
      lang: config.lang,
      email: config.email,
      passWord: password,
      uuid: config.uuid,
      ts,
      sig,
    }),
  });

  const body = await resp.json();
  if (body.errorCode !== 0) {
    throw new Error(`login failed: ${JSON.stringify(body)}`);
  }
  return body.resultBodyObject.token;
}

function ensureToken() {
  if (isMock) return Promise.resolve('mock-token');
  if (cachedToken) return Promise.resolve(cachedToken);
  if (!loginPromise) {
    loginPromise = login()
      .then((token) => {
        cachedToken = token;
        loginPromise = null;
        return token;
      })
      .catch((err) => {
        loginPromise = null;
        throw err;
      });
  }
  return loginPromise;
}

async function callProxy(token, requestPath, requestData) {
  const ts = Date.now();
  const sig = md5Hex(`${token}${config.uuid}${config.channel}${ts}${config.signKey}`);

  const resp = await fetch(apiUrl('/h5/proxy/api/request'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      channel: config.channel,
      uuid: config.uuid,
      ts,
      sig,
      data: JSON.stringify({ requestPath, requestData }),
    }),
  });

  return resp.json();
}

const createWebProxyRequest = (t) => {
  return async ({ requestPath, requestData }) => {
    let token;
    let body;
    try {
      token = await ensureToken();
      body = await callProxy(token, requestPath, requestData);
    } catch (err) {
      // 网络 / CORS / 登录失败
      Toast.info(t('text163'));
      // eslint-disable-next-line no-console
      console.error('[h5-proxy] request failed', err);
      return Promise.reject(err);
    }

    // token 过期:清缓存重登后重试一次
    if (body.errorCode === 500001) {
      cachedToken = null;
      token = await ensureToken();
      body = await callProxy(token, requestPath, requestData);
    }

    const { error, errorCode } = body;
    if (errorCode === 0 || !error) {
      return body;
    }

    switch (errorCode) {
      case -1:
        Toast.info(t('text163'));
        break;
      case 500001:
        Toast.info(t('text26'));
        break;
      case 500012:
      case 502504:
        Toast.info(t('text27'));
        break;
      case 502506:
        Toast.info(t('text28'));
        break;
      case 503001:
        break;
      case 500000:
        Toast.info(t('text29'));
        break;
      // SIM 卡管理专属错误码(后端契约: sim-management-h5-frontend.md)
      case 501608:
        Toast.info(t('text147'));
        break;
      case 501609:
        Toast.info(t('text148'));
        break;
      case 501610:
        // SIMBOSS 业务错误:由页面自行展示后端 errorMessage
        break;
      case 501611:
        Toast.info(t('text152'));
        break;
      default:
        Toast.info(t('text30'));
        break;
    }
    return Promise.reject(body);
  };
};

export default createWebProxyRequest;
